import { IconButton } from '@orbitalwatch/design';
import { useEffect, useId } from 'react';
import type { ReactNode } from 'react';
import { Icons } from './icons';

export interface SidePanelProps {
    /** h2 of the panel ("On this page", "Comments"). */
    title: ReactNode;
    open: boolean;
    onClose: () => void;
    /** Pixel width on desktop; default 352. Phones: full width. */
    width?: number;
    /** Right side of the head, before Close (a count, a toggle). */
    actions?: ReactNode;
    className?: string;
    children: ReactNode;
}

/**
 * DS gap: the reader's right-hand panel (table of contents, comments): a non-modal column on surface-1 next to the
 * content, with a head (title + Close) and a scrolling body. The library `Drawer` is modal (scrim, focus trap), which
 * the reader must not be. Esc closes it when focus is inside (`ds.css` "side panel").
 */
export function SidePanel(props: SidePanelProps) {
    const headingId = useId();
    const { open, onClose } = props;

    useEffect(() => {
        if (!open) return;
        const onKey = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            const panel = document.getElementById(headingId)?.closest('.ark-side-panel');
            if (panel && panel.contains(document.activeElement)) onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose, headingId]);

    if (!open) return null;
    return (
        <aside
            className={['ark-side-panel', props.className].filter(Boolean).join(' ')}
            style={{ ['--ark-side-panel-w' as string]: `${props.width ?? 352}px` }}
            aria-labelledby={headingId}
        >
            <div className="ark-side-panel-head">
                <h2 id={headingId}>{props.title}</h2>
                <div className="ark-side-panel-actions">
                    {props.actions}
                    <IconButton icon={<Icons.close size={16} />} aria-label="Close panel" onClick={onClose} />
                </div>
            </div>
            <div className="ark-side-panel-body">{props.children}</div>
        </aside>
    );
}
